import { Injectable } from '@angular/core';
import { GalleryItem, GalleryUploadLimits } from '@app/gallery/gallery.service';
import { Customer, UserDetails, UserTag } from '@app/user/user.service';
import { FitApiClientService, FitList } from '@shared/fit-api-client.service';
import { Subject } from 'rxjs';

//resolution center messages

export interface ResolutionMessage {
    id: number;
    created_at: string;
    available_at: string;
    from: string;
    message: string;
    seen: boolean;
    to: string;
    user_avatar?: string;
    sender: 'root' | 'owner';
    attachments: Attachment[];
    thread: ResolutionMessage[];
    is_message_deleted: boolean;
    is_message_read: boolean;
}

//client messages

export interface Thread {
    id: number;
    user: ChatUser;
    subject: string;
    total_messages: number;
    unread_messages: number;
    created_at: string;
    user_name?: string;
    user_id: number;
    is_closed: boolean;
}

export interface ThreadRequest {
    application_id?: number;
    subject: string;
    message: string;
    attachment_ids?: number[];
}

export interface ClientChat {
    id: number;
    user: ChatUser;
    last_message?: ClientMessage;
    unread_count: number;
    is_pinned: boolean;
    is_favorited: boolean;
    is_archived: boolean;
}

export interface ClientMessage {
    id: number;
    chat_id: number;
    author: ChatParticipant;
    message?: string;
    is_my_message: boolean;
    is_message_deleted: boolean;
    is_message_read: boolean;
    created_at: string;
    scheduled_at?: string;
    attachments: Attachment[];
    reactions: Reaction[];
    thread: ClientMessage[];
}

//chat room messages

export interface ChatRoom {
    id: number;
    name: string;
    reference_name?: string;
    custom_name?: string;
    description?: string;
    type: 'system' | 'custom';
    image?: GalleryItem;
    members: ChatParticipant[];
    member_count: number;
    tags: UserTag[];
    last_message?: ChatRoomMessage;
    unread_count: number;
    show_in_the_app: boolean;
    is_read_only: boolean;
    is_pinned: boolean;
    is_favorited: boolean;
    is_archived: boolean;
    is_owner: boolean;
}

export interface CustomChatRoom {
    id: number;
    name: string;
    description?: string;
    image_id?: number;
    is_read_only?: boolean;
    //show_in_the_app?: boolean;
    tag_ids?: number[];
    user_ids?: number[];
}

export interface SystemChatRoom {
    id: number;
    name: string;
    reference_name?: string;
    description?: string;
    image_id?: number;
    is_read_only?: boolean;
    show_in_the_app?: boolean;
}

export interface ChatRoomMessage {
    id: number;
    chat_id: number;
    author: ChatParticipant;
    message?: string;
    is_my_message: boolean;
    is_message_deleted: boolean;
    is_message_read?: boolean;
    created_at: string;
    scheduled_at?: string;
    attachments: Attachment[];
    reactions: Reaction[];
    thread: ClientMessage[];
}

//broadcast messages

//obsolete
export interface BroadcastRequest {
    application_id?: number;
    message: string;
    attachment_ids?: number[];
}

export interface BroadcastAudienceScopeItem {
    object_type: string;
    object_id: number;
}

export interface BroadcastAudience {
    id: number;
    audience_key: string;
    type: 'system' | 'custom';
    name: string;
    description: string;
    member_count: number;
    scope: BroadcastAudienceScopeItem[] | null;
}

export interface BroadcastAudienceMember {
    id: number;
    name: string;
    email: string;
    avatar?: string;
}

export interface BroadcastAudiencesPayload {
    system: BroadcastAudience[];
    custom: BroadcastAudience[];
}

export interface BroadcastMessageStatusBadge {
    key: string;
    label: string;
    color: string;
    status_order: number;
}

export interface BroadcastMessageSender {
    id: number;
    name: string;
}

export interface BroadcastMessage {
    id: number;
    message: string;
    scheduled_at: string;
    is_sent: boolean;
    scope_hash: string;
    status: string;
    status_badge: BroadcastMessageStatusBadge;
    recipients_count: number;
    sent_at: string;
    last_queued_at: string;
    created_at: string;
    sender: BroadcastMessageSender;
    attachments?: BroadcastAttachment[];
}

export interface BroadcastUpsertRequest {
    message: string;
    attachment_ids?: number[];
    scheduled_at?: string;
    scope: BroadcastAudienceScopeItem[];
}

//common

export interface SearchUser {
    id: number;
    name: string;
    email: string;
    avatar?: string;
    is_selected: boolean;
}

export interface ChatUser {
    id: number;
    first_name?: string;
    last_name?: string;
    email: string;
    avatar?: string;
    age?: number;
    height_in_centimeters?: number;
    weight_in_kilograms?: number;
    roles?: string[];
    tags?: UserTag[];
    chat_id?: number;
};

export interface ChatParticipant {
    id: number;
    name: string;
    email: string;
    avatar?: string;
    role: string;
    is_muted: boolean;
}

export interface ReactionUser {
    id: number;
    name: string;
    avatar?: string;
    reaction: string;
}

export interface ChatAttachmentObject {
    id: number;
    name: string;
    meta?: string | null;
    image_url?: string;
}

export interface Attachment {
    resolution_centre_attachments_id?: number;
    chat_message_attachment_id: number;
    object_type?: string;
    object_id?: number;
    attachment: AttachmentData | null;
    object?: ChatAttachmentObject | null;
    reactions: Reaction[];
}

export interface AttachmentData {
    id: number;
    name: string;
    thumbnail: string;
    type: string;
    url: string;
    mime_type?: string;
    extension?: string;
    source?: string;
    width_in_pixels?: number;
    height_in_pixels?: number;
    size?: string;
}

export interface BroadcastAttachment {
    broadcast_attachment_id: number;
    attachment: AttachmentData;
}

export interface Reaction {
    content: string;
    count: number;
    is_user_reaction: boolean;
}

export interface ReactionsBreakdown {
    reactions: Reaction[];
    users: ReactionUser[];
}

export const AVAILABLE_REACTIONS: string[] = ['🔥', '👍', '👎', '😂', '😮', '❤️', '🙏', '💪', '✅', '👏', '😍', '😥'];

export interface MessageRequest {
    application_id?: number;
    thread_id?: number;
    message_id?: number; //for updating a message
    message: string;
    available_at?: string;
    user_id?: number;
    attachment_ids?: number[];
    parent_id?: number;
    scheduled_at?: string; // ISO 8601 UTC datetime string for scheduled message delivery
}

export interface ReplyMessageRequest {
    message: string;
    attachment_ids?: number[];
}

export interface AllApplicationsUnreadMessages {
    whitelabel_application_id: number;
    whitelabel_application_name: string;
    count: number;
}

//notifications

export interface UnreadNotifications {
    count: number;
    notifications: Notification[];
}

interface Notification {
    notification_id: number;
    type: string;
    type_id: number;
    timestamp: string;
    sender: string;
    seen: boolean;
}

export type NotificationType = 'ResolutionCentreMessage'
    | 'ChatMessage'
    | 'GroupChatMessage'
    | 'GroupChatObjection'
    | 'ScheduledEvent'
    | 'LiveEvent'
    | 'LiveEventStart'
    | 'QuestionnaireResponseFilled'
    | 'NoSuitableProgramFound'
    | 'FeedPostCommentPosted';

export interface NotificationMessage {
    collapseKey: string;
    data: MessageData;
    from: string;
    messageId: string;
}

export interface HeaderNotification {
    sender: string;
    timestamp: string;
    type: string;
    seen?: boolean;
}

export interface MessageData {
    id: string;
    link: string;
    message: string;
    title: string;
    type: NotificationType;
    image: string;
    notification_id: string | number;
    cms_url: string;
    seen?: boolean;
}

/*
export interface TotalUnread {
    number_of_unread_chats: number;
    number_of_unread_resolution_centre_threads: number;
    unread_threads: any[] | HeaderNotification[] | MessageData[];
}
*/

/**
 * Utility function to convert Customer to ChatUser
 * Customer does not have age, height_in_centimeters, weight_in_kilograms fields
 */
export function customerToChatUser(customer: Customer): ChatUser {
    return {
        id: customer.id,
        first_name: customer.first_name,
        last_name: customer.last_name,
        email: customer.email,
        avatar: customer.avatar
    };
}

/**
 * Utility function to convert UserDetails to ChatUser
 * Maps image_url to avatar field
 */
export function userDetailsToChatUser(userDetails: UserDetails): ChatUser {
    return {
        id: userDetails.id,
        first_name: userDetails.first_name,
        last_name: userDetails.last_name,
        email: userDetails.email,
        avatar: userDetails.image_url,
        age: userDetails.age,
        height_in_centimeters: userDetails.height_in_centimeters,
        weight_in_kilograms: userDetails.weight_in_kilograms,
        tags: userDetails.tags
    };
}

function formatBytes(bytes, decimals) {
    if (bytes === 0) {
        return '0 Bytes';
    }
    
    const k = 1024;
    const dm = decimals <= 0 ? 0 : decimals || 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

@Injectable({
    providedIn: 'root'
})

export class ResolutionCenterService {
    constructor(private fitApi: FitApiClientService) { }

    public unreadClientMessagesNotificator = new Subject<number>();
    public updateAllAppsMessages = new Subject<any>();
    public showNewThreadForm = new Subject<boolean>();
    public updateTotalNotifications = new Subject<boolean>();
    public newMessageReceived = new Subject();
    public newDetailsResolutionCenterMessage = new Subject();

    /** Set before navigating to /client-messages to open direct chat with a user (existing or new). */
    public openDirectChatRequest: { userId: number } | null = null;

    public uploadLimits: GalleryUploadLimits = {
        image_types: [],
        upload_limit_in_bytes: 0,
        video_types: [],
        document_types: []
    };

    public formattedBytes = '';
    public threadMessageCache = {};

    //resolution center messages

    public getResolutionThreadList(applicationId: number, query?: string, showClosed = false, page: number = 1) {
        if (!applicationId) {
            return this.fitApi.get<FitList<Thread[]>>(`/admin/message/thread`, { page: page.toString(), search: query, show_closed: String(showClosed) });
        }
        
        const params = {
            application_id: applicationId.toString(),
            page: page.toString(),
            show_closed: String(showClosed)
        };

        if (query) {
            params["search"] = query;
        }

        return this.fitApi.get<FitList<Thread[]>>(`/admin/message/thread`, params);
    }

    public getResolutionMessages(appId: number, threadId: number, page: number) {
        let params;
        
        if (!appId) {
            params = {
                thread_id: threadId.toString(),
                page: page.toString()
            };
        } else {
            params = {
                application_id: appId.toString(),
                thread_id: threadId.toString(),
                page: page.toString()
            };
        }
        
        return this.fitApi.get<FitList<ResolutionMessage>>(`/admin/message`, params);
    }

    public sendResolutionMessage(message: MessageRequest, attachments: GalleryItem[]) {
        const params = {
            application_id: message.application_id,
            thread_id: message.thread_id,
            message: message.message,
            available_at: message.available_at,
            attachment_ids: attachments.map(item => item.id)
        };
        
        return this.fitApi.put<any>(`/admin/message`, params);
    }

    public markThreadAsSeen(applicationId: number, threadId: number) {
        let params: any = {};
        
        if (applicationId) {
            params = {
                application_id: applicationId,
                thread_id: threadId
            };
            return this.fitApi.post<FitList<ResolutionMessage>>(`/admin/message/read/${threadId}/${applicationId}`, {});
        } else {
            params = {
                thread_id: threadId
            };
            return this.fitApi.post<FitList<ResolutionMessage>>(`/admin/message/read/${threadId}`, {});
        }
    }

    public createNewThread(thread: ThreadRequest, attachments: GalleryItem[]) {
        let params;
        
        if (!thread.application_id) {
            params = {
                application_id: null,
                subject: thread.subject,
                message: thread.message,
                attachment_ids: attachments.map(item => item.id)
            };
        } else {
            params = {
                application_id: thread.application_id,
                subject: thread.subject,
                message: thread.message,
                attachment_ids: attachments.map(item => item.id)
            };
        }
        
        return this.fitApi.put<FitList<any>>(`/admin/message/thread`, params);
    }

    public closeThread(threadId: number, applicationId?: number) {
        let params;
        
        if (applicationId) {
            params = {
                thread_id: threadId.toString(),
                application_id: applicationId.toString()
            };
        } else {
            params = {
                thread_id: threadId.toString()
            };
        }

        return this.fitApi.put<any>(`/admin/message/thread/close`, params);
    }

    //client messages

    public getChatList(page = 1, query?: string, threadStatus?: string, selectedTags?: number[]) {
        const params: any = {
            page: page.toString()
        };

        if (query) {
            params.search = query;
        }

        if (threadStatus) {
            params.status = threadStatus;
        }

        if (selectedTags && selectedTags.length > 0) {
            selectedTags.forEach((id, index) => {
                params[`tags[${index}]`] = id.toString();
            });
        }

        return this.fitApi.get<FitList<ClientChat[]>>(`/admin/chat`, params);
    }

    public getContacts(page = 1, query?: string) {
        const params: any = {
            page: page.toString()
        };

        if (query) {
            params.search = query;
        }

        return this.fitApi.get<FitList<ChatUser>>(`/admin/chat/members`, params);
    }

    public getContactMessages(chatId: number, page = 1) {
        const params = {
            page: page.toString()
        };
        
        return this.fitApi.get<FitList<ClientMessage>>(`/admin/chat/${chatId}/messages`, params);
    }

    public getCustomerMessagesById(customerId: number, page = 1) { //obsolete
        const params = {
            page: page.toString()
        };
        
        return this.fitApi.get<FitList<ClientMessage>>(`/admin/customer/${customerId}/messages`, params);
    }

    public getScheduledChatMessages(chatId: number) {
        return this.fitApi.get<ClientMessage[]>(`/admin/chat/${chatId}/scheduled`);
    }

    public getChatById(chatId: number) {
        return this.fitApi.get<ClientChat>(`/admin/chat/${chatId}`);
    }

    public getChatByMessageId(messageId: number) {
        return this.fitApi.get<ClientChat>(`/admin/chat/by-message/${messageId}`);
    }

    /** Returns the direct chat with the given user if one exists; otherwise error/no data. */
    public getChatByUserId(userId: number) {
        return this.fitApi.get<ClientChat>(`/admin/chat/by-user/${userId}`);
    }

    public sendMessageToContact(message: MessageRequest) {
        return this.fitApi.put<FitList<ClientMessage>>(`/admin/chat/message`, message);
    }

    public sendMessageToChat(chatId: number, message: MessageRequest) {
        return this.fitApi.put<FitList<ClientMessage>>(`/admin/chat/${chatId}/message`, message);
    }

    /**
     * @returns Parent message with the reply message in the thread
     */
    public replyToMessage(messageId: number, message: ReplyMessageRequest) {
        return this.fitApi.post<ClientMessage>(`/admin/chat/message/${messageId}/reply`, message);
    }

    public getChatMessageReactions(messageId: number) {
        return this.fitApi.get<ReactionsBreakdown>(`/admin/chat/message/${messageId}/reactions`);
    }

    public toggleChatMessageReaction(messageId: number, reaction: string) {
        return this.fitApi.post<ClientMessage>(`/admin/chat/message/${messageId}/reaction`, {
            reaction: reaction
        });
    }

    public getChatMessageAttachmentReactions(attachmentId: number) {
        return this.fitApi.get<ReactionsBreakdown>(`/admin/chat/message/attachment/${attachmentId}/reactions`);
    }

    public toggleChatMessageAttachmentReaction(attachmentId: number, reaction: string) {
        return this.fitApi.post<Attachment>(`/admin/chat/message/attachment/${attachmentId}/reaction`, {
            reaction: reaction
        });
    }

    public toggleMessageReadStatus(messageId: number) {
        return this.fitApi.post<ClientMessage>(`/admin/chat/message/${messageId}/toggle-read-status`, {});
    }

    public toggleChatIsPinned(chatId: number, applicationId?: number) {
        return applicationId 
            ? this.fitApi.put<ClientChat>(`/admin/chat/${chatId}/toggle-pinned/${applicationId}`, {})
            : this.fitApi.put<ClientChat>(`/admin/chat/${chatId}/toggle-pinned`, {});
    }

    public toggleChatIsFavorited(chatId: number, applicationId?: number) {
        return applicationId 
            ? this.fitApi.put<ClientChat>(`/admin/chat/${chatId}/toggle-favorited/${applicationId}`, {})
            : this.fitApi.put<ClientChat>(`/admin/chat/${chatId}/toggle-favorited`, {});
    }

    public toggleChatsArchived(chatId: number, applicationId?: number) {
        return applicationId 
            ? this.fitApi.put<ClientChat>(`/admin/chat/${chatId}/toggle-archived/${applicationId}`, {})
            : this.fitApi.put<ClientChat>(`/admin/chat/${chatId}/toggle-archived`, {});
    }

    public cacheThread(id: number, message: string) {
        this.threadMessageCache[id] = message;
    }

    public getThreadCahedMessage(id: number): string {
        if (id in this.threadMessageCache) {
            return this.threadMessageCache[id];
        }
        
        return '';
    }

    //both client and resolution center messages

    public deleteAttachment(file: Attachment, appId: number, isClientMessaging: boolean) {
        if (isClientMessaging) {
            return this.fitApi.delete<any>(`/admin/chat/message/attachment/${file.chat_message_attachment_id}`);
        }
        
        if (appId) {
            return this.fitApi.delete<any>(`/admin/message/attachment/${file.resolution_centre_attachments_id}/${appId}`);
        }
        
        return this.fitApi.delete<any>(`/admin/message/attachment/${file.resolution_centre_attachments_id}`);
    }

    public deleteMessage(messageId: number, appId: number, isClientMessaging: boolean) {
        return isClientMessaging
            ? this.fitApi.delete<any>(`/admin/chat/message/${messageId}`)
            : this.fitApi.delete<any>(`/admin/message/${messageId}/${appId}`);
    }

    //chat rooms

    public getChatRoomList(page = 1, type: 'system' | 'custom' | null = null, threadStatus?: string, query?: string) {
        const params: any = {
            page: page.toString()
        };

        if (type) {
            params.type = type;
        }

        if (threadStatus) {
            params.status = threadStatus;
        }

        if (query) {
            params.search = query;
        }

        return this.fitApi.get<FitList<ChatRoom[]>>(`/admin/group-chat`, params);
    }

    public saveCustomChatRoom(chatRoom: CustomChatRoom, selectedTagIds?: number[], selectedUserIds?: number[]) {
        const payload: any = { ...chatRoom };
        
        if (selectedTagIds && selectedTagIds.length > 0) {
            payload.tag_ids = selectedTagIds;
        }
        
        if (selectedUserIds && selectedUserIds.length > 0) {
            payload.user_ids = selectedUserIds;
        }
        
        return this.fitApi.put<ChatRoom>(`/admin/group-chat/${chatRoom.id || 0}`, payload);
    }

    public saveSystemChatRoom(chatRoom: SystemChatRoom) {
        return this.fitApi.put<ChatRoom>(`/admin/system-chat/${chatRoom.id}`, chatRoom);
    }

    public searchUsersForAddingToChatRoom(query: string, selectedTags: number[], selectedUsers: number[], page = 1) {
        const params: any = {
            page: page.toString()
        };

        if (query) {
            params.search = query;
        }

        if (selectedTags && selectedTags.length > 0) {
            selectedTags.forEach((id, index) => {
                params[`selected_tags[${index}]`] = id.toString();
            });
        }

        if (selectedUsers && selectedUsers.length > 0) {
            selectedUsers.forEach((id, index) => {
                params[`selected_users[${index}]`] = id.toString();
            });
        }

        return this.fitApi.get<FitList<SearchUser>>(`/admin/group-chat/search-user`, params);
    }

    public getChatRoomMembers(chatRoomId: number, query?: string, page = 1) {
        const params: any = {
            page: page.toString()
        };

        if (query) {
            params.search = query;
        }

        return this.fitApi.get<FitList<ChatParticipant>>(`/admin/group-chat/${chatRoomId}/members`, params);
    }

    public addChatRoomMember(chatRoomId: number, memberId: number) {
        return this.fitApi.post<any>(`/admin/group-chat/${chatRoomId}/member/${memberId}`, {});
    }

    public removeChatRoomMember(chatRoomId: number, memberId: number) {
        return this.fitApi.delete<any>(`/admin/group-chat/${chatRoomId}/member/${memberId}`);
    }

    public toggleChatRoomIsPinned(chatRoomId: number) {
        return this.fitApi.put<ChatRoom>(`/admin/group-chat/${chatRoomId}/toggle-pinned`, {});
    }

    public toggleChatRoomIsFavorited(chatRoomId: number) {
        return this.fitApi.put<ChatRoom>(`/admin/group-chat/${chatRoomId}/toggle-favorited`, {});
    }

    public toggleChatRoomIsArchived(chatRoomId: number) {
        return this.fitApi.put<ChatRoom>(`/admin/group-chat/${chatRoomId}/toggle-archived`, {});
    }

    public deleteChatRoom(chatRoomId: number) {
        return this.fitApi.delete<any>(`/admin/group-chat/${chatRoomId}`);
    }

    public getChatRoomMessages(chatRoomId: number, page = 1) {
        const params = {
            page: page.toString()
        };
        return this.fitApi.get<FitList<ChatRoomMessage>>(`/admin/group-chat/${chatRoomId}/message`, params);
    }

    public getScheduledChatRoomMessages(chatRoomId: number) {
        return this.fitApi.get<ChatRoomMessage[]>(`/admin/group-chat/${chatRoomId}/scheduled`);
    }

    public sendMessageToChatRoom(chatRoomId: number, message: MessageRequest) {
        return this.fitApi.put<FitList<ChatRoomMessage>>(`/admin/group-chat/${chatRoomId}/message`, message);
    }

    public deleteChatRoomMessage(messageId: number) {
        return this.fitApi.delete<FitList<ChatRoomMessage>>(`/admin/group-chat/message/${messageId}`);
    }

    public deleteChatRoomAttachment(file: Attachment) {
        return this.fitApi.delete<any>(`/admin/group-chat/message/attachment/${file.chat_message_attachment_id}`);
    }

    /**
     * @returns Parent message with the reply message in the thread
     */
    public replyToMessageInChatRoom(messageId: number, message: ReplyMessageRequest) {
        return this.fitApi.post<ChatRoomMessage>(`/admin/group-chat/message/${messageId}/reply`, message);
    }

    public getChatRoomMessageReactions(messageId: number) {
        return this.fitApi.get<ReactionsBreakdown>(`/admin/group-chat/message/${messageId}/reactions`);
    }

    public toggleChatRoomMessageReaction(messageId: number, reaction: string) {
        return this.fitApi.post<ChatRoomMessage>(`/admin/group-chat/message/${messageId}/reaction`, {
            reaction: reaction
        });
    }

    public getChatRoomMessageAttachmentReactions(attachmentId: number) {
        return this.fitApi.get<ReactionsBreakdown>(`/admin/group-chat/message/attachment/${attachmentId}/reactions`);
    }

    public toggleChatRoomMessageAttachmentReaction(attachmentId: number, reaction: string) {
        return this.fitApi.post<Attachment>(`/admin/group-chat/message/attachment/${attachmentId}/reaction`, {
            reaction: reaction
        });
    }

    //broadcast messages

    //obsolete
    public broadcastMessageToAll(message: BroadcastRequest) {
        return this.fitApi.put<any>(`/admin/chat/broadcast`, message);
    }

    public getBroadcastAudiences() {
        return this.fitApi.get<BroadcastAudiencesPayload>('/admin/broadcast/audiences', {});
    }

    public getBroadcastMessages(audienceKey: string, page = 1) {
        return this.fitApi.get<FitList<BroadcastMessage>>('/admin/broadcast/messages', {
            audience: audienceKey,
            page: String(page),
        });
    }

    public getBroadcastMembers(audienceKey: string, search?: string, page = 1) {
        const params: Record<string, string> = {
            audience: audienceKey,
            page: String(page),
        };

        if (search?.trim()) {
            params.search = search.trim();
        }

        return this.fitApi.get<FitList<BroadcastAudienceMember>>('/admin/broadcast/members', params);
    }

    public upsertBroadcastMessage(id: number, body: BroadcastUpsertRequest) {
        return this.fitApi.put<BroadcastMessage>(`/admin/broadcast/${id}`, body);
    }

    public getScheduledBroadcastMessages(audienceKey: string) {
        return this.fitApi.get<BroadcastMessage[]>('/admin/broadcast/scheduled', {
            audience: audienceKey,
        });
    }

    public deleteBroadcastMessage(id: number) {
        return this.fitApi.delete<BroadcastMessage>(`/admin/broadcast/${id}`);
    }

    //common

    public getUploadLimits() {
        this.fitApi.get<GalleryUploadLimits>('/admin/gallery/upload-limits').then(res => {
            this.uploadLimits = res.data;
            this.formattedBytes = this.formatBytes(this.uploadLimits.upload_limit_in_bytes, 2);
        });
    }

    public uploadAttachment(fileName: string, file: File, application_id: number) {
        const form = new FormData();
        form.append('url', file);
        // Strip extension from filename
        const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');
        form.append('name', nameWithoutExtension);
        form.append('is_user_avatar', 'false');
        return this.fitApi.uploadFileAttachment<GalleryItem>(form, application_id);
    }

    public uploadGifAttachment(gifUrl: string, gifName: string, application_id: number) {
        const form = new FormData();
        form.append('url', gifUrl); // URL as string, not File
        form.append('name', gifName);
        form.append('source', 'giphy');
        form.append('is_user_avatar', 'false');
        return this.fitApi.uploadFileAttachment<GalleryItem>(form, application_id);
    }

    public formatBytes(bytes, decimals) {
        return formatBytes(bytes, decimals);
    }

    public isValidFileType(file: File, acceptedType: string) {
        const typeValidator = (types: string[]) => types.some(type => file.type.toLowerCase().indexOf(type) > -1);
        return typeValidator(this.getAcceptableFileTypes(acceptedType));
    }

    public getAcceptableFileTypes(acceptedType: string) {
        switch (acceptedType) {
            case 'image':
                return this.uploadLimits.image_types;
            case 'video':
                return this.uploadLimits.video_types;
            case 'all':
                return [...this.uploadLimits.image_types, ...this.uploadLimits.video_types];
        }
    }

    public getRefinedMessage(message: string) {
        const params: any = {
            text: message,
        };

        return this.fitApi.get<any>('/admin/ai-assistant/rewrite-content', params);
    }

    //notifications

    public clearAllNotifications() {
        return this.fitApi.post<any>(`/admin/notification/seen-all`, {});
    }

    public getUnreadNotifications() {
        return this.fitApi.get<UnreadNotifications>(`/admin/notification`);
    }

    public markNotificationSeen(notification_id: number) {
        return this.fitApi.put<any>(`/admin/notification/${notification_id}/seen`, { notification_id });
    }

    public getAllAplicationsUnreadMessages() {
        return this.fitApi.get<AllApplicationsUnreadMessages[]>(`/admin/message/unread-per-app`);
    }

    public clearIntervalUnreadMessages() {
        clearInterval(this._interval);
    }

    public countDistinctUsersByTags(tagIds?: number[], selectedUserIds?: number[]): Promise<{ data: number | null; error: any }> {
        if ((!tagIds || tagIds.length === 0) && (!selectedUserIds || selectedUserIds.length === 0)) {
            return Promise.resolve({ data: 0, error: null });
        }

        const params: any = {};
        if (tagIds && tagIds.length > 0) {
            tagIds.forEach((id, index) => {
                params[`tag_ids[${index}]`] = id.toString();
            });
        }

        if (selectedUserIds && selectedUserIds.length > 0) {
            selectedUserIds.forEach((id, index) => {
                params[`user_ids[${index}]`] = id.toString();
            });
        }

        return this.fitApi.get<{ data: number }>(`/admin/group-chat/count-user`, params)
            .then(response => {
                if (response.error) {
                    return { data: null, error: response.error };
                }
                const count = typeof response.data === 'number' ? response.data : 0;
                return { data: count, error: null };
            })
            .catch(error => {
                return { data: null, error };
            });
    }

    private _interval = null;
}
